import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
} from '@mui/material';
import { has, pick, set } from 'lodash';
import { useMemo, useState } from 'react';
import { SidebarAccordion } from 'src/components/accordion';
import { InputTextLabel } from 'src/components/input/Input.styled';
import {
  ColorPickerWidget,
  DisclaimerPositionButtonsWidget,
  InputWidget,
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { HeaderWidget } from 'src/components/widget/HeaderWidget/HeaderWidget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import { GCF_DEFAULT_VALUES } from 'src/constants/gcfComponentConfig';
import { isDy } from 'src/features/components/editComponentPage/EditComponentPage.helper';
import { useGetProductVariationTypes, useGetProjectConfig } from 'src/hooks';
import {
  GcfPriceLabelShapeTypeEnum,
  GcfPriceLabelTypeEnum,
  IGcfProductListGroups,
  ISizesConfig,
  SizesConfigName,
} from 'src/interfaces';
import { generateEnumOptions, lang } from 'src/utils';

import { GcfPriceDisplayFormat } from '../../FormSelector.interface';
import { CategoryConfigAccordionList } from '../categoryConfigAccordionList';
import {
  ProductMoleculeFontsProvider,
  ProductMoleculeItem,
} from '../productMoleculeItem/ProductMoleculeItem';
import {
  HeaderTextStylingOptions,
  IHeaderTextStylingAccordion,
} from './HeaderTextStylingAccordion.interface';

export const HeaderTextStylingAccordion = ({
  componentConfig,
  handleOnChange,
  errors,
  enableIndividualStyles,
  theme,
  hidden,
}: IHeaderTextStylingAccordion) => {
  const defaultCheckedValue =
    !componentConfig?.productList?.config?.hasOwnProperty('sizes') ||
    componentConfig.productList.config.sizes?.defaultConfig;
  const [isChecked, setIsChecked] = useState(Boolean(defaultCheckedValue));
  const productList = (componentConfig?.productList ?? {}) as any;

  const { data: productVariationSizes = [] } = useGetProductVariationTypes([
    'sort=*name',
  ]);

  const { data: projectConfig } = useGetProjectConfig();

  const mappedFonts = useMemo(() => {
    const projectFonts =
      projectConfig?.fonts?.map((item) => ({
        label: item.name,
        value: item.name,
      })) ?? [];

    return projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;
  }, [projectConfig?.fonts]);

  const handleChange = (event: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) => {
    setIsChecked(event.target.checked);

    if (event.target.checked) {
      if (
        !has(
          componentConfig,
          'componentConfig.productList.config.sizes.defaultConfig',
        )
      ) {
        set(
          { ...componentConfig.productList },
          'config.sizes.defaultConfig',
          {},
        );
      }
      handleOnChange(
        'productList',
        set(
          { ...componentConfig.productList },
          'config.sizes',
          pick(componentConfig?.productList?.config?.sizes, 'defaultConfig'),
        ),
      );
    } else {
      // map default to all sizes
      const sizes = productVariationSizes.reduce((acc, size) => {
        acc[size.name] = structuredClone(
          componentConfig?.productList?.config?.sizes?.defaultConfig,
        ) as ISizesConfig;

        return acc;
      }, {} as SizesConfigName);

      handleOnChange(
        'productList',
        set({ ...componentConfig.productList }, 'config.sizes', sizes),
      );
    }
  };

  const isDyProductSource = isDy(componentConfig.productsSource as any);
  function handleSubHeaderDescription(key?: any, value?: any) {
    const updatedProductList = {
      ...productList,
      groups:
        productList.groups &&
        Object.entries(productList.groups as IGcfProductListGroups).reduce(
          (acc, [groupId, groupData]) => {
            acc[groupId] = {
              ...groupData,
              config: {
                ...groupData.config,
                groupHeader: {
                  ...(groupData.config?.groupHeader as any),
                  description: {
                    ...(groupData.config?.groupHeader?.description ?? {}),
                    [key]: value,
                  },
                },
              },
            };

            return acc;
          },
          {} as IGcfProductListGroups,
        ),
      config: {
        ...productList.config,
        groupHeader: {
          ...(productList.config?.groupHeader ?? {}),
          styles: {
            ...(productList.config?.groupHeader?.styles ?? {}),
            description: {
              ...(productList.config?.groupHeader?.styles?.description || {}),
              [key]: value,
            },
          },
        },
      },
    };

    handleOnChange(`productList`, updatedProductList);
  }

  function handleSubHeader(key?: any, value?: any) {
    const updatedProductList = {
      ...productList,
      groups:
        productList.groups &&
        Object.entries(productList.groups as IGcfProductListGroups).reduce(
          (acc, [groupId, groupData]) => {
            acc[groupId] = {
              ...groupData,
              config: {
                ...groupData.config,
                groupHeader: {
                  ...(groupData.config?.groupHeader as any),
                  [key]: value,
                },
              },
            };

            return acc;
          },
          {} as IGcfProductListGroups,
        ),
      config: {
        ...productList.config,
        groupHeader: {
          ...(productList.config?.groupHeader ?? {}),
          styles: {
            ...(productList.config?.groupHeader?.styles ?? {}),
            [key]: value,
          },
        },
      },
    };

    handleOnChange(`productList`, updatedProductList);
  }

  const titleItems = [
    {
      id: HeaderTextStylingOptions.HEADER,
      label: lang.headerAndDescription,
      hasError: errors?.header?.text ? true : false,
      content: (
        <Grid container spacing={2} style={{ padding: '26px' }}>
          <HeaderWidget
            componentConfig={componentConfig}
            handleOnChange={handleOnChange}
            errors={errors}
            theme={theme}
            hidden={hidden}
          />
        </Grid>
      ),
    },
    {
      id: HeaderTextStylingOptions.SUBHEADER,
      label: lang.subheaderAndDescription,
      hasError: errors?.productList?.config?.groupHeader?.styles?.text
        ? true
        : false,
      content: (
        <Grid container spacing={2} style={{ padding: '26px' }}>
          <Grid item xs={12}>
            <InputTextLabel>{lang.subheader}</InputTextLabel>
          </Grid>
          <Grid item xs={6}>
            <SelectDropdownWidget
              label={lang.fontFamily}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.font ?? theme?.config.subheader?.font
              }
              onChange={(value) => handleSubHeader('font', value)}
              selectOptions={mappedFonts}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.font
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.font
                  ?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.fontSize}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.fontSize ?? theme?.config.subheader?.fontSize
              }
              onChange={(value) => {
                handleSubHeader('fontSize', value);
              }}
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.fontSize
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.fontSize
                  ?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <ColorPickerWidget
              label={lang.fontColor}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.color ?? theme?.config.subheader?.color
              }
              onChange={(value) => handleSubHeader('color', value)}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.color
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.color
                  ?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.lineHeight}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.lineHeight ?? theme?.config.subheader?.lineHeight
              }
              onChange={(value) => handleSubHeader('lineHeight', value)}
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.lineHeight
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.lineHeight
                  ?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.marginBelow}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.marginBottom
              }
              onChange={(value) => handleSubHeader('marginBottom', value)}
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.marginBottom
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.marginBottom
                  ?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.height}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.height ?? theme?.config.subheader?.height
              }
              onChange={(value) => handleSubHeader('height', value)}
            />
          </Grid>

          <Grid item xs={12}>
            <InputTextLabel>{lang.pill}</InputTextLabel>
          </Grid>
          <Grid item xs={6}>
            <ColorPickerWidget
              label={lang.backgroundColor}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.backgroundColor ?? theme?.config.subheader?.backgroundColor
              }
              onChange={(value) => handleSubHeader('backgroundColor', value)}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles
                  ?.backgroundColor
              }
              error={
                errors?.productList?.config?.groupHeader?.styles
                  ?.backgroundColor?.message || false
              }
              options={{
                hideAIcon: true,
                backgroundColor: true,
                border: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <ColorPickerWidget
              label={lang.borderColor}
              value={(
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.border ?? theme?.config.subheader?.border
              )
                ?.split(/[, ]+/)
                .pop()}
              onChange={(value) =>
                handleSubHeader('border', `2px solid ${value}`)
              }
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.border
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.border
                  ?.message || false
              }
              options={{
                hideAIcon: true,
                backgroundColor: true,
                border: true,
              }}
              pickerWrapperStyle={{ left: '-50px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.borderRadius}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.borderRadius ?? theme?.config.subheader?.borderRadius
              }
              onChange={(value) => handleSubHeader('borderRadius', value)}
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.borderRadius
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.borderRadius
                  ?.message || false
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <InputTextLabel>{lang.subheaderDescription}</InputTextLabel>
          </Grid>
          <Grid item xs={6}>
            <SelectDropdownWidget
              label={lang.fontFamily}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.description?.font ??
                theme?.config.subheaderDescription?.font
              }
              onChange={(value) => handleSubHeaderDescription('font', value)}
              selectOptions={mappedFonts}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.description
                  ?.font
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.description
                  ?.font?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.fontSize}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.description?.fontSize ??
                theme?.config.subheaderDescription?.fontSize
              }
              onChange={(value) => {
                handleSubHeaderDescription('fontSize', value);
              }}
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.description
                  ?.fontSize
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.description
                  ?.fontSize?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <ColorPickerWidget
              label={lang.fontColor}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.description?.color ??
                theme?.config.subheaderDescription?.color
              }
              onChange={(value) => handleSubHeaderDescription('color', value)}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.description
                  ?.color
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.description
                  ?.color?.message || false
              }
              colorPickerMode={'block'}
              options={{ triangle: 'hide' }}
              pickerWrapperStyle={{ left: '0px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.lineHeight}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.description?.lineHeight ??
                theme?.config.subheaderDescription?.lineHeight
              }
              onChange={(value) =>
                handleSubHeaderDescription('lineHeight', value)
              }
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.description
                  ?.lineHeight
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.description
                  ?.lineHeight?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.marginBelow}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.description?.marginBottom
              }
              onChange={(value) =>
                handleSubHeaderDescription('marginBottom', value)
              }
              options={{ suffix: 'px' }}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.description
                  ?.marginBottom
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.description
                  ?.marginBottom?.message || false
              }
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.height}
              value={
                componentConfig?.productList?.config?.groupHeader?.styles
                  ?.description?.height ??
                theme?.config.subheaderDescription?.height
              }
              onChange={(value) => handleSubHeaderDescription('height', value)}
              hasError={
                !!errors?.productList?.config?.groupHeader?.styles?.description
                  ?.height
              }
              error={
                errors?.productList?.config?.groupHeader?.styles?.description
                  ?.height?.message || false
              }
            />
          </Grid>
        </Grid>
      ),
    },
    {
      id: HeaderTextStylingOptions.DISCLAIMER,
      label: lang.componentDisclaimer,
      hasError: errors?.disclaimer?.text ? true : false,
      content: (
        <Grid container spacing={2} style={{ padding: '26px' }}>
          <Grid item xs={12}>
            <SwitchButtonWidget
              value={componentConfig?.disclaimer?.visible}
              onChange={(value) => handleOnChange('disclaimer.visible', value)}
            />
          </Grid>
          {componentConfig?.disclaimer?.visible && (
            <>
              <Grid item xs={12}>
                <InputWidget
                  label={lang.disclaimerText}
                  value={componentConfig?.disclaimer?.text}
                  onChange={(value) => handleOnChange('disclaimer.text', value)}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectDropdownWidget
                  label={lang.fontFamily}
                  value={
                    componentConfig?.disclaimer?.font ??
                    theme?.config.disclaimer?.font
                  }
                  onChange={(value) => handleOnChange('disclaimer.font', value)}
                  selectOptions={mappedFonts}
                  hasError={!!errors?.disclaimer?.font}
                  error={errors?.disclaimer?.font?.message || false}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputWidget
                  label={lang.fontSize}
                  value={
                    componentConfig?.disclaimer?.fontSize ??
                    theme?.config.disclaimer?.fontSize
                  }
                  onChange={(value) =>
                    handleOnChange('disclaimer.fontSize', value)
                  }
                  options={{ suffix: 'px' }}
                  hasError={!!errors?.disclaimer?.fontSize}
                  error={errors?.disclaimer?.fontSize?.message || false}
                />
              </Grid>
              <Grid item xs={12}>
                <ColorPickerWidget
                  label={lang.fontColor}
                  value={
                    componentConfig?.disclaimer?.color ??
                    theme?.config.disclaimer?.color
                  }
                  onChange={(value) =>
                    handleOnChange('disclaimer.color', value)
                  }
                  hasError={!!errors?.disclaimer?.color}
                  error={errors?.disclaimer?.color?.message || false}
                  options={{ triangle: 'hide' }}
                  colorPickerMode="block"
                  pickerWrapperStyle={{ left: '0' }}
                />
              </Grid>
              <Grid item xs={6}>
                <DisclaimerPositionButtonsWidget
                  label={lang.orientation}
                  value={componentConfig?.disclaimer?.position}
                  onChange={(value) =>
                    handleOnChange('disclaimer.position', value)
                  }
                  hasError={!!errors?.disclaimer?.position}
                  error={errors?.disclaimer?.position?.message || false}
                />
              </Grid>
            </>
          )}
        </Grid>
      ),
    },
    {
      id: HeaderTextStylingOptions.MOLECULE,
      label: lang.productMoleculeStyling,
      hasError: !!errors?.productList?.config?.sizes,
      content: (
        <ProductMoleculeFontsProvider mappedFonts={mappedFonts}>
          <>
            <Grid container spacing={2} style={{ padding: '26px' }}>
              <Grid item xs={12}>
                <SelectDropdownWidget
                  label={lang.subProductDisplayFormatType}
                  value={
                    componentConfig?.productList?.config
                      ?.subProductsDisplayFormat?.type ??
                    GCF_DEFAULT_VALUES.subProductsDisplayFormatType
                  }
                  hasError={
                    !!errors?.productList?.config?.subProductsDisplayFormat
                  }
                  error={
                    errors?.productList?.config?.subProductsDisplayFormat
                      ?.message || false
                  }
                  selectOptions={[
                    {
                      label: 'In-line',
                      value: GcfPriceDisplayFormat.INLINE,
                    },
                    {
                      label: 'Multi-line',
                      value: GcfPriceDisplayFormat.MULTILINE,
                    },
                  ]}
                  onChange={(value) =>
                    handleOnChange(
                      'productList.config.subProductsDisplayFormat.type',
                      value,
                    )
                  }
                  disabled={!!componentConfig?.isFlavourCycling}
                />
              </Grid>
              {!isDyProductSource && (
                <>
                  <Grid item xs={12}>
                    <SelectDropdownWidget
                      label={lang.sizeLabelType}
                      value={
                        componentConfig?.productList?.config?.sizeLabel?.type ??
                        GCF_DEFAULT_VALUES.priceLabelType
                      }
                      hasError={!!errors?.productList?.config?.sizeLabel}
                      error={
                        errors?.productList?.config?.sizeLabel?.message || false
                      }
                      selectOptions={generateEnumOptions(GcfPriceLabelTypeEnum)}
                      onChange={(value) =>
                        handleOnChange(
                          'productList.config.sizeLabel.type',
                          value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SelectDropdownWidget
                      label={lang.sizeLabelShape}
                      value={
                        componentConfig?.productList?.config?.sizeLabel
                          ?.shape ?? GCF_DEFAULT_VALUES.priceLabelShapeType
                      }
                      hasError={!!errors?.productList?.config?.sizeLabel?.shape}
                      error={
                        errors?.productList?.config?.sizeLabel?.shape
                          ?.message || false
                      }
                      selectOptions={generateEnumOptions(
                        GcfPriceLabelShapeTypeEnum,
                      )}
                      onChange={(value) =>
                        handleOnChange(
                          'productList.config.sizeLabel.shape',
                          value,
                        )
                      }
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <ProductMoleculeItem
                  title={lang.productName}
                  value={{
                    ...theme?.config.products,
                    ...componentConfig?.productList?.config?.products?.styles,
                  }}
                  handleChange={(value, field) =>
                    handleOnChange(
                      `productList.config.products.styles.${field}` as any,
                      value,
                    )
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>

            {enableIndividualStyles && (
              <FormGroup style={{ padding: '0 26px 15px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={lang.allSizes}
                />
              </FormGroup>
            )}
            <CategoryConfigAccordionList
              isApplyAll={isChecked}
              value={componentConfig?.productList?.config?.sizes}
              variationSizes={productVariationSizes}
              errors={errors}
              onChange={(value) => {
                handleOnChange('productList.config.sizes', value);
              }}
              theme={theme}
              options={{
                hideDisplayLabel: !!hidden?.includes(
                  HeaderTextStylingOptions.MOLECULE_DISPLAY_LABEL,
                ),
                enableIndividualStyles,
              }}
            />
          </>
        </ProductMoleculeFontsProvider>
      ),
    },
  ];

  return (
    <SidebarAccordion
      items={titleItems.filter((item) => !hidden?.includes(item.id))}
    />
  );
};
